import React, { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  KeyRound,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
} from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError } from "@/api/client";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, TextInput } from "@/admin/_primitives";

/**
 * BetaCodesAdmin — `/admin/beta-codes`.
 *
 * Wave 5-Late ANCHOR — Beta sub-domain. Executive redesign pass:
 *   • Wrapped in `AdminLayout` (title / subtitle / actions).
 *   • Every list surface hosted in a `Section` primitive with explicit
 *     hairline dividers (`border-b`, `--ds-color-admin-border`), tight
 *     card radius (`--ds-radius-card`, 4px).
 *   • Inputs sit on `--ds-radius-input` (3px) + `--ds-color-input-border`
 *     for the crisp form-field density calibrated in Wave 5-Early.
 *   • Section headings via the `Heading` DS primitive.
 *   • Status pills consume the `Badge` primitive with semantic tones —
 *     no more inline `success-soft`/`danger-soft` background juggling.
 *
 * Business logic, API surface, permission gating, sort/filter behavior,
 * CSV export, UTM builder, and every `data-testid` are preserved
 * BYTE-EXACT for downstream selectors and RTL contracts.
 */

/* ------------------------------------------------------------------ */
/* Section / Field / Input — mirror the Wave 5-Early admin primitives.  */
/* ------------------------------------------------------------------ */


function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <p
          className="text-[11px] mt-1 leading-snug"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {hint}
        </p>
      )}
    </label>
  );
}




/* ------------------------------------------------------------------ */
/* Root — /admin/beta-codes                                             */
/* ------------------------------------------------------------------ */

export default function BetaCodesAdmin() {
  const { has, isOwner } = useAdminPermissions();
  const canEdit = isOwner || has("edit_site_settings");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [perfOpen, setPerfOpen] = useState(new Set());

  function togglePerf(codeId) {
    setPerfOpen((prev) => {
      const next = new Set(prev);
      if (next.has(codeId)) next.delete(codeId);
      else next.add(codeId);
      return next;
    });
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (showActiveOnly) params.is_active = true;
      const { data } = await api.get("/admin/beta-codes", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleActive(item) {
    if (!canEdit) return;
    try {
      await api.patch(`/admin/beta-codes/${item.id}`, { is_active: !item.is_active });
      toast.success(`Access code ${item.code} ${item.is_active ? "deactivated" : "activated"}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function remove(item) {
    if (!canEdit) return;
    setRemoveBusy(true);
    try {
      await api.delete(`/admin/beta-codes/${item.id}`);
      toast.success(`Access code ${item.code} deleted.`);
      setConfirmingRemove(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRemoveBusy(false);
    }
  }

  const sectionBadge = (
    <div className="flex items-center gap-3">
      <label
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <input
          type="checkbox"
          data-testid="admin-beta-codes-active-toggle"
          checked={showActiveOnly}
          onChange={(e) => setShowActiveOnly(e.target.checked)}
          className="accent-[var(--ds-color-accent)]"
        />
        Active only
      </label>
      <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--ds-color-ink-muted)" }}>
        {total} total
      </span>
    </div>
  );

  const headerActions = (
    <>
      <GhostButton data-testid="admin-beta-codes-refresh" onClick={load}>
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </GhostButton>
      {canEdit && (
        <AccentButton data-testid="admin-beta-codes-create-btn" onClick={() => setShowCreate(true)}>
          <Plus className="w-3.5 h-3.5" />
          New code
        </AccentButton>
      )}
    </>
  );

  return (
    <AdminLayout
      title="Early Access Codes"
      subtitle="Gate signups to invited cohorts. Codes are validated case-insensitively at /api/auth/register."
      actions={headerActions}
    >
      <div data-testid="admin-beta-codes-page">
        <div className="mb-4">
          <AdminPageHelp
            title="Early Access Codes"
            purpose="Manage the invite-only access codes given to early users during private testing periods."
            configures="Access code strings, expiration dates, usage limits per code, associated notes."
            impact="Users who enter a valid, unexpired code during signup can bypass the waitlist gate."
            safety="Codes can be deactivated or extended at any time. Deactivation is immediate; deactivated codes cannot be reactivated but can be recreated."
          />
        </div>

        <Section
          title="Access codes"
          icon={KeyRound}
          badge={sectionBadge}
          testid="section-beta-codes-list"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto">
            <table
              className="w-full text-[13px]"
              style={{ borderCollapse: "collapse" }}
              data-testid="admin-beta-codes-table"
            >
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2 w-[26%]">Code</th>
                  <th className="px-4 py-2">Cohort</th>
                  <th className="px-4 py-2">Uses</th>
                  <th className="px-4 py-2">Expires</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No access codes yet.
                    </td>
                  </tr>
                )}
                {items.map((item) => {
                  const remaining =
                    item.max_uses == null ? "∞" : Math.max(0, item.max_uses - (item.uses_count || 0));
                  const isPerfOpen = perfOpen.has(item.id);
                  return (
                    <Fragment key={item.id}>
                      <tr
                        style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                        data-testid={`admin-beta-code-row-${item.code}`}
                      >
                        <td className="px-4 py-1.5 font-mono text-[var(--ds-color-ink)]">
                          <span className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`admin-beta-code-perf-toggle-${item.code}`}
                              onClick={() => togglePerf(item.id)}
                              className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-accent)]"
                              title="Show invite performance"
                            >
                              {isPerfOpen ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <KeyRound
                              className="w-3.5 h-3.5"
                              style={{ color: "var(--ds-color-accent)" }}
                            />
                            {item.code}
                          </span>
                        </td>
                        <td className="px-4 py-1.5" style={{ color: "var(--ds-color-ink-muted)" }}>
                          {item.cohort_label}
                        </td>
                        <td className="px-4 py-1.5 tabular-nums" style={{ color: "var(--ds-color-ink)" }}>
                          <span data-testid={`admin-beta-code-uses-${item.code}`}>
                            {item.uses_count || 0}
                          </span>
                          <span style={{ color: "var(--ds-color-ink-muted)" }}>
                            {" "}/ {item.max_uses == null ? "∞" : item.max_uses}
                          </span>
                          <span
                            className="ml-2 text-[10px]"
                            style={{ color: "var(--ds-color-ink-subtle)" }}
                          >
                            ({remaining} left)
                          </span>
                        </td>
                        <td className="px-4 py-1.5" style={{ color: "var(--ds-color-ink-muted)" }}>
                          {item.expires_at ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="w-3 h-3" aria-hidden="true" />
                              {new Date(item.expires_at).toLocaleDateString()}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-1.5">
                          <Badge
                            tone={item.is_active ? "successMuted" : "neutralMuted"}
                            size="sm"
                            data-testid={`admin-beta-code-status-${item.code}`}
                            className="uppercase tracking-[0.14em]"
                          >
                            <span
                              aria-hidden="true"
                              className="inline-block w-1.5 h-1.5 rounded-full"
                              style={{
                                background: item.is_active
                                  ? "var(--ds-color-success-dot)"
                                  : "var(--ds-color-neutral-dot)",
                              }}
                            />
                            {item.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-1.5 text-right">
                          {canEdit && (
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                data-testid={`admin-beta-code-invite-${item.code}`}
                                onClick={() => setInviteFor(item)}
                                className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-accent)]"
                                title="Build invite URL with UTM params"
                              >
                                <Link2 className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                data-testid={`admin-beta-code-toggle-${item.code}`}
                                onClick={() => toggleActive(item)}
                                className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                                title={item.is_active ? "Deactivate" : "Activate"}
                              >
                                {item.is_active ? (
                                  <ToggleRight className="w-4 h-4" />
                                ) : (
                                  <ToggleLeft className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                type="button"
                                data-testid={`admin-beta-code-delete-${item.code}`}
                                onClick={() => setConfirmingRemove(item)}
                                className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-danger)]"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isPerfOpen && (
                        <tr
                          data-testid={`admin-beta-code-perf-row-${item.code}`}
                          style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                        >
                          <td
                            colSpan={6}
                            className="p-0"
                            style={{ background: "var(--ds-color-admin-surface-muted)" }}
                          >
                            <InvitePerformancePanel code={item.code} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        {showCreate && (
          <CreateCodeDialog
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              load();
            }}
          />
        )}

        {inviteFor && <InviteUrlDialog item={inviteFor} onClose={() => setInviteFor(null)} />}

        <ConfirmDialog
          open={!!confirmingRemove}
          onCancel={() => setConfirmingRemove(null)}
          onConfirm={() => confirmingRemove && remove(confirmingRemove)}
          busy={removeBusy}
          variant="destructive"
          title={confirmingRemove ? `Delete access code ${confirmingRemove.code}?` : "Delete access code"}
          description="Deleting this access code is permanent. Users who haven't already redeemed it will no longer be able to sign up with it."
          confirmLabel="Delete access code"
          testId="beta-codes-delete-confirm"
        />
      </div>
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* CreateCodeDialog — modal wrapped in the admin card palette.          */
/* ------------------------------------------------------------------ */

function CreateCodeDialog({ onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [cohort, setCohort] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!code.trim() || !cohort.trim()) {
      toast.error("Code and cohort label are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        code: code.trim().toUpperCase(),
        cohort_label: cohort.trim(),
        notes: notes.trim() || undefined,
      };
      if (maxUses) body.max_uses = parseInt(maxUses, 10);
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      await api.post("/admin/beta-codes", body);
      toast.success(`Access code ${body.code} created.`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15,18,24,0.72)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 12px 32px -12px rgba(15,18,24,0.28), 0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-4 py-3.5 border-b flex items-center justify-between"
          style={{ borderColor: "var(--ds-color-admin-border)" }}
        >
          <Heading level={3} size="sm" weight="semibold" className="leading-none">
            New access code
          </Heading>
          <button
            type="button"
            data-testid="admin-beta-codes-create-close"
            onClick={onClose}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <Field label="Code (uppercase)">
            <TextInput
              data-testid="admin-beta-codes-create-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="INFORMED-COHORT-A"
              className="font-mono"
            />
          </Field>
          <Field label="Cohort label">
            <TextInput
              data-testid="admin-beta-codes-create-cohort"
              value={cohort}
              onChange={(e) => setCohort(e.target.value)}
              placeholder="Cohort A"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max uses (blank = ∞)">
              <TextInput
                data-testid="admin-beta-codes-create-max-uses"
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
              />
            </Field>
            <Field label="Expires at">
              <TextInput
                data-testid="admin-beta-codes-create-expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Notes (internal)">
            <textarea
              data-testid="admin-beta-codes-create-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
              style={{ borderColor: "var(--ds-color-input-border)" }}
            />
          </Field>
          <div
            className="flex items-center justify-end gap-2 pt-3 border-t"
            style={{ borderColor: "var(--ds-color-admin-border)" }}
          >
            <GhostButton type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </GhostButton>
            <AccentButton
              type="submit"
              data-testid="admin-beta-codes-create-submit"
              disabled={submitting}
              onClick={submit}
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create code
            </AccentButton>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* InviteUrlDialog — UTM builder wrapped in the admin card palette.     */
/* ------------------------------------------------------------------ */

function InviteUrlDialog({ item, onClose }) {
  const defaultCampaign = (item.cohort_label || item.code || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("email");
  const [campaign, setCampaign] = useState(defaultCampaign);
  const [content, setContent] = useState("");

  const baseUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/signup`;
  }, []);

  const inviteUrl = useMemo(() => {
    if (!baseUrl) return "";
    const params = new URLSearchParams();
    params.set("code", item.code);
    if (source.trim()) params.set("utm_source", source.trim());
    if (medium.trim()) params.set("utm_medium", medium.trim());
    if (campaign.trim()) params.set("utm_campaign", campaign.trim());
    if (content.trim()) params.set("utm_content", content.trim());
    return `${baseUrl}?${params.toString()}`;
  }, [baseUrl, item.code, source, medium, campaign, content]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite URL copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15,18,24,0.72)" }}
      onClick={onClose}
      data-testid="admin-beta-code-invite-dialog"
    >
      <div
        className="w-full max-w-xl rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 12px 32px -12px rgba(15,18,24,0.28), 0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-4 py-3.5 border-b flex items-start justify-between gap-3"
          style={{ borderColor: "var(--ds-color-admin-border)" }}
        >
          <div className="min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Early Access · Launch Prep
            </div>
            <Heading level={3} size="sm" weight="semibold" className="leading-tight">
              Invite URL ·{" "}
              <span className="font-mono" style={{ color: "var(--ds-color-accent-strong)" }}>
                {item.code}
              </span>
            </Heading>
            <p className="text-[11px] mt-1" style={{ color: "var(--ds-color-ink-muted)" }}>
              Cohort: {item.cohort_label} · UTM params snapshot onto the user record at /signup.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-beta-codes-invite-close"
            onClick={onClose}
            className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="utm_source">
              <TextInput
                data-testid="admin-beta-codes-invite-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="podcast, twitter, newsletter…"
              />
            </Field>
            <Field label="utm_medium">
              <TextInput
                data-testid="admin-beta-codes-invite-medium"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
                placeholder="email, social, paid…"
              />
            </Field>
            <Field label="utm_campaign">
              <TextInput
                data-testid="admin-beta-codes-invite-campaign"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
              />
            </Field>
            <Field label="utm_content (optional)">
              <TextInput
                data-testid="admin-beta-codes-invite-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="ep-12, banner-a, footer…"
              />
            </Field>
          </div>

          <Field label="Shareable URL">
            <div className="flex items-stretch gap-2">
              <TextInput
                readOnly
                data-testid="admin-beta-codes-invite-url"
                value={inviteUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 font-mono text-[12px]"
              />
              <AccentButton data-testid="admin-beta-codes-invite-copy" onClick={copy}>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </AccentButton>
            </div>
          </Field>

          <p
            className="text-[11px] leading-relaxed"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            UTM params land in{" "}
            <span className="font-mono">user.acquisition_source / medium / campaign / content</span>{" "}
            on registration, and tag every analytics event from this session for the Cohort/Source
            breakdown widget.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* InvitePerformancePanel — expandable per-row analytics.               */
/* ------------------------------------------------------------------ */

function InvitePerformancePanel({ code }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeSynthetic, setIncludeSynthetic] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "signups", dir: "desc" });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/admin/beta-codes/${encodeURIComponent(code)}/invites`, {
        params: { include_synthetic: includeSynthetic },
      })
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, includeSynthetic]);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    const dir = sortBy.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[sortBy.key] ?? (typeof a[sortBy.key] === "number" ? 0 : "");
      const bv = b[sortBy.key] ?? (typeof b[sortBy.key] === "number" ? 0 : "");
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, sortBy]);

  function toggleSort(key) {
    setSortBy((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: "desc" };
    });
  }

  function exportCsv() {
    if (!data) return;
    const headers = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "first_seen_at",
      "clicks",
      "signups",
      "redemption_rate",
      "profile_completion_rate",
      "credit_report_upload_rate",
      "feedback_submission_rate",
    ];
    const lines = [headers.join(",")];
    for (const r of sortedRows) {
      lines.push(
        headers
          .map((h) => {
            const v = r[h];
            if (v === null || v === undefined) return "";
            const s = String(v).replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
          })
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invite-performance-${code}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <div
        className="px-5 py-4 text-[12px]"
        style={{ color: "var(--ds-color-ink-muted)" }}
        data-testid={`admin-beta-code-perf-loading-${code}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />
        Loading invite performance…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="px-5 py-4 text-[12px]"
        style={{ color: "var(--ds-color-danger)" }}
        data-testid={`admin-beta-code-perf-error-${code}`}
      >
        {error}
      </div>
    );
  }
  const isEmpty =
    !data || (data.rows.length === 0 && data.totals.clicks === 0 && data.totals.signups === 0);

  return (
    <div className="px-5 py-4" data-testid={`admin-beta-code-perf-panel-${code}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div
          className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-semibold"
          style={{ color: "var(--ds-color-accent-strong)" }}
        >
          <BarChart3 className="w-3.5 h-3.5" />
          Invite Performance · UTM breakdown
        </div>
        <div className="flex items-center gap-3">
          <label
            className="inline-flex items-center gap-2 text-[11px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            <input
              type="checkbox"
              data-testid={`admin-beta-code-perf-include-synthetic-${code}`}
              checked={includeSynthetic}
              onChange={(e) => setIncludeSynthetic(e.target.checked)}
              className="accent-[var(--ds-color-accent)]"
            />
            Include synthetic seed data
          </label>
          <GhostButton
            data-testid={`admin-beta-code-perf-export-${code}`}
            onClick={exportCsv}
            disabled={isEmpty}
            className="!py-1 !text-[11px]"
          >
            <Download className="w-3 h-3" />
            CSV
          </GhostButton>
        </div>
      </div>

      {isEmpty ? (
        <div
          className="rounded-[var(--ds-radius-field)] border px-4 py-6 text-center text-[12px]"
          style={{
            borderStyle: "dashed",
            borderColor: "var(--ds-color-admin-border)",
            color: "var(--ds-color-ink-muted)",
            background: "var(--ds-color-surface)",
          }}
          data-testid={`admin-beta-code-perf-empty-${code}`}
        >
          No invite traffic yet for this code. Share the invite URL on a podcast / Twitter /
          Facebook to start tracking.
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-[var(--ds-radius-field)]"
          style={{
            border: "1px solid var(--ds-color-admin-border)",
            background: "var(--ds-color-surface)",
          }}
          data-testid={`admin-beta-code-perf-table-${code}`}
        >
          <table className="w-full text-[12px]" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr
                className="text-left text-[10px] uppercase tracking-[0.14em] font-semibold"
                style={{
                  color: "var(--ds-color-ink-subtle)",
                  background: "var(--ds-color-admin-surface-muted)",
                  borderBottom: "1px solid var(--ds-color-admin-border)",
                }}
              >
                {[
                  ["utm_source", "Source"],
                  ["utm_medium", "Medium"],
                  ["utm_campaign", "Campaign"],
                  ["utm_content", "Content"],
                  ["first_seen_at", "First seen"],
                  ["clicks", "Clicks"],
                  ["signups", "Signups"],
                  ["redemption_rate", "Redeem %"],
                  ["profile_completion_rate", "Profile %"],
                  ["credit_report_upload_rate", "Report %"],
                  ["feedback_submission_rate", "Feedback %"],
                ].map(([k, label]) => (
                  <th
                    key={k}
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => toggleSort(k)}
                    data-testid={`admin-beta-code-perf-sort-${code}-${k}`}
                  >
                    {label}
                    {sortBy.key === k ? (sortBy.dir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr
                className="font-medium"
                style={{ background: "var(--ds-color-accent-soft)" }}
                data-testid={`admin-beta-code-perf-totals-${code}`}
              >
                <td className="px-3 py-2" colSpan={4} style={{ color: "var(--ds-color-accent-strong)" }}>
                  Totals
                </td>
                <td className="px-3 py-2" style={{ color: "var(--ds-color-ink-muted)" }}>—</td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">{data.totals.clicks}</td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">{data.totals.signups}</td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                  {fmtPct(data.totals.redemption_rate)}
                </td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                  {fmtPct(data.totals.profile_completion_rate)}
                </td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                  {fmtPct(data.totals.credit_report_upload_rate)}
                </td>
                <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                  {fmtPct(data.totals.feedback_submission_rate)}
                </td>
              </tr>
              {sortedRows.map((r, i) => (
                <tr
                  key={i}
                  style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                >
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {r.utm_source ?? "(direct)"}
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {r.utm_medium ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {r.utm_campaign ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {r.utm_content ?? "—"}
                  </td>
                  <td className="px-3 py-2" style={{ color: "var(--ds-color-ink-muted)" }}>
                    {r.first_seen_at ? new Date(r.first_seen_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">{r.clicks}</td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">{r.signups}</td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">{fmtPct(r.redemption_rate)}</td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                    {fmtPct(r.profile_completion_rate)}
                  </td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                    {fmtPct(r.credit_report_upload_rate)}
                  </td>
                  <td className="px-3 py-2 text-[var(--ds-color-ink)]">
                    {fmtPct(r.feedback_submission_rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}
